import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NO_RADIUS, RADIUS_LABEL_MAPPING } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { LandCoverChangeExplainer } from '@/components/NCExplainers/LandCoverChangeExplainer';
import { COMPLIANCE_LOGOS_LAND_COVER_CHANGE } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { LandCoverChangeGraphTile } from './GraphTile';
import { LandCoverChangeStatsTile } from './StatsTile';

/** Plot-level (no landscape radius) variant of the Land cover change card for views without MapRadiusContext. */
export const LandCoverChangeCard = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.landCoverChange.labels.landCoverChange')}>
      <PlotLandCoverChangeCard />
    </PlotCardWrapper>
  );
};

const PlotLandCoverChangeCard = () => {
  const { t } = useTranslation();

  const factName = IndicatorName.landcover_by_landscape_type_historic_pct_graph;

  const facts = usePlotFacts([factName], undefined, undefined, {
    [PlotReportFactLabelNameEnum.landscape_radius_m]: RADIUS_LABEL_MAPPING[`${NO_RADIUS}`],
  });

  const fact = getFactFromFactArray<GraphFact[]>(facts, factName);

  if (!fact || isNullish(fact.value)) {
    return null;
  }

  const cardFact = fact as PlotReportFactElement<GraphFact[]>;

  return (
    <NCCard explainer={<LandCoverChangeExplainer />} data-testid='land-cover-change-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.landCoverChange.labels.landCoverChange')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <LandCoverChangeGraphTile landCoverChange={cardFact} radius={NO_RADIUS} />
        <LandCoverChangeStatsTile landCoverChange={cardFact} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_LAND_COVER_CHANGE} />
    </NCCard>
  );
};
